import { makeAutoObservable, runInAction } from "mobx";
import type { RootStore } from "@intrasync/stores";
import type {
  DeductionProfile,
  DeductionLineItem,
  DeductionProfileData,
  DeductionLineItemData,
  DeductionType,
  EmployeeType,
} from "../../../types";
import { DEDUCTION_TYPES, EMPLOYEE_TYPES } from "../../../types";

/**
 * Deduction Profiles Store
 * Manages deduction profile templates for payroll
 * KEY FEATURE: Single deduction code routes to different GLs based on employee type (shop vs office)
 */
class DeductionProfilesStore {
  rootStore: RootStore;

  // UI State
  selectedProfileId: string | null = null;
  isProfileDialogOpen = false;
  isLineItemDialogOpen = false;
  editingProfile: Partial<DeductionProfile> | null = null;
  editingLineItem: Partial<DeductionLineItem> | null = null;
  searchQuery = "";
  filterActive = true;
  filterEmployeeType: EmployeeType | "all" | null = null;
  isLoading = false;
  isSaving = false;

  // DataGrid configuration
  columns = [
    { key: "name", label: "Profile Name", width: 200 },
    { key: "description", label: "Description", width: 200 },
    { key: "employeeType", label: "Employee Type", width: 120 },
    { key: "companyId", label: "Company", width: 120 },
    { key: "itemCount", label: "Items", width: 80, align: "center" as const },
    { key: "isDefault", label: "Default", width: 80, align: "center" as const },
    { key: "isActive", label: "Active", width: 80, align: "center" as const },
  ];

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  // ==================== Computed Properties ====================

  get profiles(): DeductionProfile[] {
    const store = this.rootStore.serviceStores?.["deduction-profiles"];
    return (store?.entities as DeductionProfile[]) || [];
  }

  get lineItems(): DeductionLineItem[] {
    const store = this.rootStore.serviceStores?.["deduction-line-items"];
    return (store?.entities as DeductionLineItem[]) || [];
  }

  get filteredProfiles(): DeductionProfile[] {
    let result = this.profiles;

    if (this.filterActive) {
      result = result.filter((p) => p.isActive);
    }

    if (this.filterEmployeeType) {
      result = result.filter(
        (p) => p.employeeType === this.filterEmployeeType || p.employeeType === "all"
      );
    }

    if (this.searchQuery) {
      const query = this.searchQuery.toLowerCase();
      result = result.filter(
        (p) =>
          p.name.toLowerCase().includes(query) ||
          p.description?.toLowerCase().includes(query)
      );
    }

    return result;
  }

  get filteredCount(): number {
    return this.filteredProfiles.length;
  }

  get selectedProfile(): DeductionProfile | undefined {
    if (!this.selectedProfileId) return undefined;
    return this.profiles.find((p) => p.deductionProfileId === this.selectedProfileId);
  }

  get selectedProfileLineItems(): DeductionLineItem[] {
    if (!this.selectedProfileId) return [];
    return this.lineItems
      .filter((li) => li.deductionProfileId === this.selectedProfileId)
      .sort((a, b) => a.sortOrder - b.sortOrder);
  }

  get gridData() {
    return this.filteredProfiles.map((profile) => ({
      id: profile.deductionProfileId,
      name: profile.name,
      description: profile.description || "-",
      employeeType: this.getEmployeeTypeLabel(profile.employeeType),
      companyId: profile.companyId,
      itemCount: this.lineItems.filter(
        (li) => li.deductionProfileId === profile.deductionProfileId
      ).length,
      isDefault: profile.isDefault ? "Yes" : "No",
      isActive: profile.isActive ? "Active" : "Inactive",
    }));
  }

  get deductionTypeOptions() {
    return DEDUCTION_TYPES.map((dt) => ({
      value: dt.type,
      label: dt.label,
      description: dt.description,
      hasEmployerPortion: dt.hasEmployerPortion,
      isGovernmentMandated: dt.isGovernmentMandated,
    }));
  }

  get employeeTypeOptions() {
    return [
      { value: "all", label: "All Employees" },
      ...EMPLOYEE_TYPES.map((et) => ({
        value: et.type,
        label: et.label,
      })),
    ];
  }

  // ==================== Actions ====================

  setSearchQuery = (query: string) => {
    this.searchQuery = query;
  };

  setFilterActive = (active: boolean) => {
    this.filterActive = active;
  };

  setFilterEmployeeType = (type: EmployeeType | "all" | null) => {
    this.filterEmployeeType = type;
  };

  setSelectedProfileId = (id: string | null) => {
    this.selectedProfileId = id;
  };

  // Profile Dialog Actions
  openCreateProfileDialog = () => {
    this.editingProfile = {
      name: "",
      description: "",
      companyId: "",
      employeeType: "all",
      isActive: true,
      isDefault: false,
    };
    this.isProfileDialogOpen = true;
  };

  openEditProfileDialog = (profile: DeductionProfile) => {
    this.editingProfile = { ...profile };
    this.isProfileDialogOpen = true;
  };

  closeProfileDialog = () => {
    this.isProfileDialogOpen = false;
    this.editingProfile = null;
  };

  updateEditingProfile = (field: keyof DeductionProfile, value: unknown) => {
    if (this.editingProfile) {
      (this.editingProfile as Record<string, unknown>)[field] = value;
    }
  };

  // Line Item Dialog Actions
  openCreateLineItemDialog = () => {
    if (!this.selectedProfileId) return;
    this.editingLineItem = {
      deductionProfileId: this.selectedProfileId,
      deductionCode: "",
      deductionType: "federal_tax" as DeductionType,
      description: "",
      employeeGlAccountId: "",
      employerGlAccountId: "",
      shopEmployeeGlAccountId: "",
      shopEmployerGlAccountId: "",
      showEveryTime: true,
      isActive: true,
      sortOrder: this.selectedProfileLineItems.length + 1,
    };
    this.isLineItemDialogOpen = true;
  };

  openEditLineItemDialog = (lineItem: DeductionLineItem) => {
    this.editingLineItem = { ...lineItem };
    this.isLineItemDialogOpen = true;
  };

  closeLineItemDialog = () => {
    this.isLineItemDialogOpen = false;
    this.editingLineItem = null;
  };

  updateEditingLineItem = (field: keyof DeductionLineItem, value: unknown) => {
    if (this.editingLineItem) {
      (this.editingLineItem as Record<string, unknown>)[field] = value;
    }
  };

  // ==================== API Actions ====================

  loadProfiles = async () => {
    this.isLoading = true;
    try {
      const profileStore = this.rootStore.serviceStores?.["deduction-profiles"];
      const lineItemStore = this.rootStore.serviceStores?.["deduction-line-items"];

      await Promise.all([
        profileStore?.find({ query: { $limit: 1000 } }),
        lineItemStore?.find({ query: { $limit: 5000 } }),
      ]);
    } catch (error) {
      console.error("Failed to load deduction profiles:", error);
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  saveProfile = async (): Promise<boolean> => {
    if (!this.editingProfile) return false;

    this.isSaving = true;
    try {
      const profileStore = this.rootStore.serviceStores?.["deduction-profiles"];

      const data: DeductionProfileData = {
        name: this.editingProfile.name || "",
        description: this.editingProfile.description,
        companyId: this.editingProfile.companyId || "",
        employeeType: this.editingProfile.employeeType || "all",
        isActive: this.editingProfile.isActive ?? true,
        isDefault: this.editingProfile.isDefault ?? false,
      };

      if (this.editingProfile.deductionProfileId) {
        await profileStore?.patch(this.editingProfile.deductionProfileId, data);
      } else {
        await profileStore?.create(data);
      }

      this.closeProfileDialog();
      await this.loadProfiles();
      return true;
    } catch (error) {
      console.error("Failed to save deduction profile:", error);
      return false;
    } finally {
      runInAction(() => {
        this.isSaving = false;
      });
    }
  };

  deleteProfile = async (profileId: string): Promise<boolean> => {
    try {
      const profileStore = this.rootStore.serviceStores?.["deduction-profiles"];
      await profileStore?.remove(profileId);
      await this.loadProfiles();
      if (this.selectedProfileId === profileId) {
        this.selectedProfileId = null;
      }
      return true;
    } catch (error) {
      console.error("Failed to delete deduction profile:", error);
      return false;
    }
  };

  saveLineItem = async (): Promise<boolean> => {
    if (!this.editingLineItem) return false;

    this.isSaving = true;
    try {
      const lineItemStore = this.rootStore.serviceStores?.["deduction-line-items"];

      const data: DeductionLineItemData = {
        deductionProfileId: this.editingLineItem.deductionProfileId || "",
        deductionCode: this.editingLineItem.deductionCode || "",
        deductionType: this.editingLineItem.deductionType || "federal_tax",
        description: this.editingLineItem.description || "",
        employeeGlAccountId: this.editingLineItem.employeeGlAccountId || "",
        employerGlAccountId: this.editingLineItem.employerGlAccountId,
        shopEmployeeGlAccountId: this.editingLineItem.shopEmployeeGlAccountId,
        shopEmployerGlAccountId: this.editingLineItem.shopEmployerGlAccountId,
        showEveryTime: this.editingLineItem.showEveryTime ?? true,
        isActive: this.editingLineItem.isActive ?? true,
        sortOrder: this.editingLineItem.sortOrder || 0,
        calculationType: this.editingLineItem.calculationType,
        rate: this.editingLineItem.rate,
        maxAmount: this.editingLineItem.maxAmount,
        annualLimit: this.editingLineItem.annualLimit,
      };

      if (this.editingLineItem.deductionLineItemId) {
        await lineItemStore?.patch(this.editingLineItem.deductionLineItemId, data);
      } else {
        await lineItemStore?.create(data);
      }

      this.closeLineItemDialog();
      await this.loadProfiles();
      return true;
    } catch (error) {
      console.error("Failed to save deduction line item:", error);
      return false;
    } finally {
      runInAction(() => {
        this.isSaving = false;
      });
    }
  };

  deleteLineItem = async (lineItemId: string): Promise<boolean> => {
    try {
      const lineItemStore = this.rootStore.serviceStores?.["deduction-line-items"];
      await lineItemStore?.remove(lineItemId);
      await this.loadProfiles();
      return true;
    } catch (error) {
      console.error("Failed to delete deduction line item:", error);
      return false;
    }
  };

  // ==================== Utility Methods ====================

  getDeductionTypeLabel = (type: DeductionType): string => {
    const config = DEDUCTION_TYPES.find((dt) => dt.type === type);
    return config?.label || type;
  };

  getDeductionTypeConfig = (type: DeductionType) => {
    return DEDUCTION_TYPES.find((dt) => dt.type === type);
  };

  getEmployeeTypeLabel = (type: EmployeeType | "all"): string => {
    if (type === "all") return "All";
    const config = EMPLOYEE_TYPES.find((et) => et.type === type);
    return config?.label || type;
  };

  /**
   * Get the correct GL account for a deduction based on employee type
   * This is the KEY FEATURE that solves the duplicate deduction code problem
   */
  getGLAccountForEmployee = (
    lineItem: DeductionLineItem,
    employeeType: EmployeeType,
    portion: "employee" | "employer"
  ): string => {
    if (employeeType === "shop") {
      // Shop employees use shop-specific GLs if available
      if (portion === "employee" && lineItem.shopEmployeeGlAccountId) {
        return lineItem.shopEmployeeGlAccountId;
      }
      if (portion === "employer" && lineItem.shopEmployerGlAccountId) {
        return lineItem.shopEmployerGlAccountId;
      }
    }

    // Default to standard GLs
    return portion === "employee"
      ? lineItem.employeeGlAccountId
      : lineItem.employerGlAccountId || "";
  };

  duplicateProfile = async (profileId: string): Promise<boolean> => {
    const profile = this.profiles.find((p) => p.deductionProfileId === profileId);
    if (!profile) return false;

    try {
      const profileStore = this.rootStore.serviceStores?.["deduction-profiles"];
      const lineItemStore = this.rootStore.serviceStores?.["deduction-line-items"];

      // Create new profile
      const newProfile = await profileStore?.create({
        name: `${profile.name} (Copy)`,
        description: profile.description,
        companyId: profile.companyId,
        employeeType: profile.employeeType,
        isActive: true,
        isDefault: false,
      });

      if (newProfile?.deductionProfileId) {
        // Copy line items
        const profileLineItems = this.lineItems.filter(
          (li) => li.deductionProfileId === profileId
        );

        await Promise.all(
          profileLineItems.map((li) =>
            lineItemStore?.create({
              deductionProfileId: newProfile.deductionProfileId,
              deductionCode: li.deductionCode,
              deductionType: li.deductionType,
              description: li.description,
              employeeGlAccountId: li.employeeGlAccountId,
              employerGlAccountId: li.employerGlAccountId,
              shopEmployeeGlAccountId: li.shopEmployeeGlAccountId,
              shopEmployerGlAccountId: li.shopEmployerGlAccountId,
              showEveryTime: li.showEveryTime,
              isActive: li.isActive,
              sortOrder: li.sortOrder,
              calculationType: li.calculationType,
              rate: li.rate,
              maxAmount: li.maxAmount,
              annualLimit: li.annualLimit,
            })
          )
        );
      }

      await this.loadProfiles();
      return true;
    } catch (error) {
      console.error("Failed to duplicate profile:", error);
      return false;
    }
  };

  /**
   * Create standard deduction profile with common deductions
   * Pre-populated with federal, state, social security, medicare, LNI, etc.
   */
  createStandardProfile = async (
    companyId: string,
    employeeType: EmployeeType | "all"
  ): Promise<boolean> => {
    try {
      const profileStore = this.rootStore.serviceStores?.["deduction-profiles"];
      const lineItemStore = this.rootStore.serviceStores?.["deduction-line-items"];

      const typeName =
        employeeType === "all" ? "" : employeeType === "shop" ? " (Shop)" : " (Office)";

      const newProfile = await profileStore?.create({
        name: `Standard Deductions${typeName}`,
        description: `Standard deduction profile for ${companyId}${typeName}`,
        companyId,
        employeeType,
        isActive: true,
        isDefault: employeeType === "all",
      });

      if (newProfile?.deductionProfileId) {
        // Create standard line items
        const standardDeductions = [
          { type: "federal_tax", code: "FED_TAX", desc: "Federal Income Tax" },
          { type: "state_tax", code: "STATE_TAX", desc: "State Income Tax" },
          { type: "social_security", code: "SS", desc: "Social Security" },
          { type: "medicare", code: "MED", desc: "Medicare" },
          { type: "lni", code: "LNI", desc: "Labor & Industries" },
          { type: "paid_family_leave", code: "PFML", desc: "Paid Family & Medical Leave" },
          { type: "long_term_care", code: "LTC", desc: "Long Term Care (WA Cares)" },
        ];

        await Promise.all(
          standardDeductions.map((ded, index) =>
            lineItemStore?.create({
              deductionProfileId: newProfile.deductionProfileId,
              deductionCode: ded.code,
              deductionType: ded.type,
              description: ded.desc,
              employeeGlAccountId: "", // To be filled in by user
              employerGlAccountId: "",
              showEveryTime: true,
              isActive: true,
              sortOrder: index + 1,
            })
          )
        );
      }

      await this.loadProfiles();
      return true;
    } catch (error) {
      console.error("Failed to create standard profile:", error);
      return false;
    }
  };
}

export default DeductionProfilesStore;
